import React, { useState, useRef, useEffect } from 'react';
import { MicrophoneIcon, StopIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import WaveSurfer from 'wavesurfer.js';
import { useAuth } from '../context/AuthContext';
import SignInPrompt from '../components/SignInPrompt';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

export default function RecordMessage() {
  const { user } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    occasion: '',
    recipient: '',
    tags: ''
  });

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);

  useEffect(() => {
    if (audioBlob && waveformRef.current) {
      // Initialize WaveSurfer
      wavesurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: '#3b82f6',
        progressColor: '#1e40af',
        cursorColor: '#1e40af',
        height: 80,
        responsive: true
      });

      const url = URL.createObjectURL(audioBlob);
      wavesurferRef.current.load(url);

      wavesurferRef.current.on('finish', () => {
        setIsPlaying(false);
      });

      return () => {
        if (wavesurferRef.current) {
          wavesurferRef.current.destroy();
        }
      };
    }
  }, [audioBlob]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/mp3' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const togglePlayback = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
      setIsPlaying(!isPlaying);
    }
  };

  const handleUpload = async () => {
    if (!audioBlob || !formData.title) {
      alert('Please record audio and provide a title');
      return;
    }

    if (!user) {
      alert('Please log in to upload recordings');
      return;
    }
    
    setUploading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', audioBlob, 'recording.mp3');
      formDataToSend.append('title', formData.title);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('occasion', formData.occasion);
      formDataToSend.append('recipient_id', formData.recipient);
      formDataToSend.append('creator_id', user.id);

      const response = await axios.post(
        `${API_URL}/recordings/upload`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      alert('Message uploaded successfully!');
      
      // Reset form
      setAudioBlob(null);
      setFormData({
        title: '',
        category: '',
        occasion: '',
        recipient: '',
        tags: ''
      });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload message. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Show sign-in prompt if user tries to record without being logged in
  if (!user && audioBlob) {
    return <SignInPrompt feature="save your recording" />;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Record a Message</h1>
        
        {!user && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              👋 You can try recording, but you'll need to sign in to save your message.
            </p>
          </div>
        )}

        {/* Recording Controls */}
        <div className="mb-8">
          <div className="flex flex-col items-center space-y-4">
            {!isRecording && !audioBlob && (
              <button
                onClick={startRecording}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <MicrophoneIcon className="h-6 w-6 mr-2" />
                Start Recording
              </button>
            )}

            {isRecording && (
              <div className="text-center">
                <div className="inline-flex items-center px-6 py-3 bg-red-100 rounded-full mb-4">
                  <div className="h-3 w-3 bg-red-600 rounded-full animate-pulse mr-3"></div>
                  <span className="text-red-600 font-medium">Recording...</span>
                </div>
                <button
                  onClick={stopRecording}
                  className="block mx-auto px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-sm text-white bg-gray-800 hover:bg-gray-900"
                >
                  <StopIcon className="h-6 w-6 inline mr-2" />
                  Stop Recording
                </button>
              </div>
            )}

            {audioBlob && (
              <div className="w-full space-y-4">
                <div ref={waveformRef} className="w-full"></div>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={togglePlayback}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    {isPlaying ? (
                      <>
                        <PauseIcon className="h-5 w-5 mr-2" />
                        Pause
                      </>
                    ) : (
                      <>
                        <PlayIcon className="h-5 w-5 mr-2" />
                        Play
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setAudioBlob(null);
                      setIsPlaying(false);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Re-record
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Message Details Form */}
        {audioBlob && (
          <div className="space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Message Title *
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="e.g., Advice for Sarah's Wedding"
              />
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="">Select category</option>
                  <option value="advice">Advice</option>
                  <option value="memory">Memory</option>
                  <option value="love_letter">Love Letter</option>
                  <option value="birthday">Birthday</option>
                  <option value="wedding">Wedding</option>
                  <option value="graduation">Graduation</option>
                  <option value="tough_times">Tough Times</option>
                </select>
              </div>

              <div>
                <label htmlFor="occasion" className="block text-sm font-medium text-gray-700">
                  Occasion
                </label>
                <input
                  type="text"
                  id="occasion"
                  value={formData.occasion}
                  onChange={(e) => setFormData({ ...formData, occasion: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="e.g., Wedding Day"
                />
              </div>
            </div>

            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="e.g., family, love, wisdom"
              />
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setAudioBlob(null)}
                className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Save Message'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
